/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { KTSVG, toAbsoluteUrl } from "../../../helpers";
import { useNotifications } from "../../../../app/common/hooks/useNotifications";
import { Notification } from "../../../../app/common/services/notification.service";
import { MenuComponent } from "../../../assets/ts/components";

const HeaderNotificationsMenu: React.FC = () => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    totalCount,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    setShowUnreadOnly,
  } = useNotifications();

  const [activeTab, setActiveTab] = useState("updates");

  const handleNotificationClick = async (notification: Notification) => {
    console.log("Notification clicked:", notification);

    try {
      // Mark as read if not already read
      if (!notification.isRead) {
        await markAsRead(notification._id);
      }

      // Navigate to action URL if provided
      if (notification.actionUrl) {
        window.location.href = notification.actionUrl;
      }
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const handleMarkAllRead = async () => {
    console.log("Mark All Read clicked");
    try {
      await markAllAsRead();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleRefresh = async () => {
    console.log("Refresh clicked");
    try {
      await refreshNotifications();
    } catch (error) {
      console.error("Error refreshing notifications:", error);
    }
  };

  const handleTabChange = (tab: string, event: React.MouseEvent) => {
    event.preventDefault();
    setActiveTab(tab);
    setShowUnreadOnly(false);
  };

  const displayNotifications = notifications || [];

  // Ensure menu components are properly initialized
  useEffect(() => {
    const timer = setTimeout(() => {
      MenuComponent.reinitialization();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>
        {`
          .notification-item {
            transition: all 0.2s ease-in-out;
          }
          .notification-item:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          }
          .nav-line-tabs .nav-link:hover {
            opacity: 0.8 !important;
          }
          .menu .tab-content {
            background-color: #ffffff;
          }
        `}
      </style>
      <div
        className="menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px"
        data-kt-menu="true"
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e4e6ef",
          borderRadius: "8px",
        }}
      >
        <div
          className="d-flex flex-column bgi-no-repeat rounded-top"
          style={{
            backgroundImage: `url('${toAbsoluteUrl(
              "/media/misc/pattern-1.jpg"
            )}')`,
          }}
        >
          <h3 className="text-black fw-bold px-9 mt-10 mb-6">
            Notifications
            {totalCount > 0 && (
              <span className="fs-8 opacity-75 ps-3">
                {totalCount}{" "}
                {totalCount === 1 ? "notification" : "notifications"}
              </span>
            )}
            {unreadCount > 0 && (
              <span className="badge badge-light-danger fs-8 ms-2">
                {unreadCount} new
              </span>
            )}
          </h3>

          <ul className="nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-bold px-9">
            <li className="nav-item">
              <a
                className={clsx(
                  "nav-link text-black opacity-100 opacity-hover-100 pb-4 cursor-pointer",
                  activeTab === "updates" && "active"
                )}
                onClick={(e) => handleTabChange("updates", e)}
              >
                Updates
              </a>
            </li>
          </ul>
        </div>

        <div className="tab-content">
          <div
            className={clsx(
              "tab-pane fade",
              activeTab === "updates" && "show active"
            )}
            role="tabpanel"
          >
            <div className="scroll-y mh-325px my-5 px-8">
              {loading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <div className="text-danger">{error}</div>
                  <button
                    className="btn btn-sm btn-primary mt-2"
                    onClick={handleRefresh}
                  >
                    Retry
                  </button>
                </div>
              ) : displayNotifications.length > 0 ? (
                displayNotifications.map((notification, index) => (
                  <div
                    key={notification._id || index}
                    className={clsx(
                      "d-flex flex-stack py-4 px-3 cursor-pointer rounded mx-2 mb-2",
                      notification.isRead
                        ? "bg-light-secondary hover-bg-light-primary"
                        : "bg-light-primary hover-bg-primary",
                      "notification-item"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      transition: "all 0.2s ease",
                      minHeight: "60px",
                      backgroundColor: notification.isRead
                        ? "#f8f9fa"
                        : "#e3f2fd",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        notification.isRead ? "#e9ecef" : "#bbdefb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        notification.isRead ? "#f8f9fa" : "#e3f2fd";
                    }}
                  >
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="symbol symbol-35px me-4">
                        <span
                          className={clsx(
                            "symbol-label",
                            notification.isRead ? "bg-secondary" : "bg-primary"
                          )}
                        >
                          <KTSVG
                            path="/media/icons/duotune/general/gen007.svg"
                            className="svg-icon-2 text-black"
                          />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <div
                          className={clsx(
                            "fs-6 fw-bolder mb-1",
                            notification.isRead
                              ? "text-gray-700"
                              : "text-gray-900"
                          )}
                        >
                          {notification.title}
                        </div>
                        <div className="text-gray-600 fs-7">
                          {notification.message}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column align-items-end ms-3">
                      <span
                        className={clsx(
                          "badge fs-8 mb-1",
                          notification.isRead
                            ? "badge-secondary"
                            : "badge-light"
                        )}
                      >
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </span>
                      {!notification.isRead && (
                        <span className="badge badge-danger fs-8">New</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10">
                  <div className="text-gray-600 fs-6 fw-bold mb-4">
                    No notifications yet
                  </div>
                  <div className="text-gray-400 fs-7">
                    You'll see notifications here when there are updates
                  </div>
                </div>
              )}
            </div>

            {displayNotifications.length > 0 && (
              <div
                className="py-3 text-center border-top"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <button
                  className="btn btn-sm btn-light-primary me-3"
                  onClick={handleMarkAllRead}
                  style={{
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #2196f3",
                    color: "#1976d2",
                  }}
                >
                  Mark All Read
                </button>
                <button
                  className="btn btn-sm btn-light-secondary"
                  onClick={handleRefresh}
                  style={{
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #9e9e9e",
                    color: "#424242",
                  }}
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export { HeaderNotificationsMenu };
